import styled from "styled-components";

import { PNG } from "@ryde/assets";
import {
  IStyledAttributes,
  LAYOUT_RIGHT_HEIGHT_INCREASE_MOBILE,
  LAYOUT_RIGHT_HEIGHT_WITH_FOOTER,
  LAYOUT_RIGHT_HEIGHT_WITHOUT_FOOTER,
  LAYOUT_RIGHT_IMAGE_AREA_WIDTH,
} from "@ryde/shared";
import {
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

const calcHeight = (
  showFooter: boolean | undefined,
  showHeader: boolean | undefined,
  isMobile: boolean | undefined,
): string => {
  const mobileHeightIncrease = isMobile
    ? LAYOUT_RIGHT_HEIGHT_INCREASE_MOBILE
    : 0;

  if (showHeader) {
    return showFooter
      ? `${LAYOUT_RIGHT_HEIGHT_WITH_FOOTER + mobileHeightIncrease}px`
      : `${LAYOUT_RIGHT_HEIGHT_WITHOUT_FOOTER + mobileHeightIncrease}px`;
  }

  return showFooter
    ? `${LAYOUT_RIGHT_HEIGHT_WITHOUT_FOOTER + mobileHeightIncrease}px`
    : `${mobileHeightIncrease}px`;
};

interface ILayoutRightContent {
  $showFooter?: boolean;
  $showHeader?: boolean;
  bgPng?: string;
  $showBackgroundImage?: boolean;
  $isMobile?: boolean;
  $calculatedHeight?: string;
  $contentNoScroll?: boolean;
}

export const LayoutRightContentStyled = styled.div.attrs<
  IStyledAttributes & ILayoutRightContent
>((props: ILayoutRightContent) => ({
  "data-e2e": "layout-right-content",
  $calculatedHeight: calcHeight(
    props.$showFooter,
    props.$showHeader,
    props.$isMobile,
  ),
}))<ILayoutRightContent>`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  min-height: calc(100vh - ${({ $calculatedHeight }) => $calculatedHeight});
  overflow: ${({ $contentNoScroll }) => ($contentNoScroll ? "hidden" : "auto")};
  height: ${({ $contentNoScroll }) => ($contentNoScroll ? "100%" : "0")};
  ${({ $contentNoScroll }) => ($contentNoScroll ? "min-height: 0;" : "")}
  padding-bottom: 40px;
  padding-inline-end: ${({ $showBackgroundImage: showBackgroundImage }) =>
    showBackgroundImage ? LAYOUT_RIGHT_IMAGE_AREA_WIDTH : "0"};
  background-attachment: fixed;
  background-image: ${({ $showBackgroundImage, bgPng }) => {
    if ($showBackgroundImage) {
      return bgPng ? `url(${bgPng})` : `url(${PNG.LayoutRightBg2PNG})`;
    }

    return "";
  }};
  background-repeat: no-repeat;
  background-position: bottom ${props => (props.$showFooter ? "64px" : "0")}
    right 25px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    background-image: none;
    padding-inline-end: 10px;
    padding-inline-end: 0;
    padding-bottom: 0px;
  }
`;

export const LayoutRightHeaderStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "layout-right-header",
})`
  position: sticky;
  top: 0;
  min-height: 56px;
  width: 100%;
  background-color: ${rydeColors.white};
  border-bottom: 1px solid ${greyMui._300};
  display: flex;
  align-items: center;
  padding-inline-start: 25px;

  transition:
    filter 0.55s ease-out,
    backdrop-filter 0.55s ease-out;
  &.inner-item-editing {
    filter: blur(2px) grayscale(100%);
    backdrop-filter: blur(3px);
    pointer-events: none;
  }

  svg {
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
  }
`;

export const LayoutRightHeaderTitleStyled = styled.h2.attrs<IStyledAttributes>({
  "data-e2e": "layout-right-header-title",
})`
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  color: ${greyMui._900};
  span.sub_header {
    font-size: 16px;
    margin-inline-start: 10px;
    line-height: 1.2;
    vertical-align: middle;
    color: ${greyMui._500};
  }

  span.sub_header.animated_sub_header {
    opacity: 0;
    transform: translateY(-2px);
    animation: fadeInUp 2.5s ease forwards;
  }

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const LayoutRightHeaderStartButtonsStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "layout-right-header-start-buttons",
  },
)`
  display: flex;
  align-items: center;
  .close {
    width: 15px;
    height: 15px;
    margin-inline-end: 15px;
    cursor: pointer;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    .close {
      margin-inline-end: 8px;
    }
  }
`;

export const LayoutRightFooterStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "layout-right-footer",
})`
  height: 64px;
  position: absolute;
  z-index: ${ScreenDepth.LAYOUT_RIGHT_FOOTER};
  bottom: 0;
  width: 100%;
  display: flex;
  align-items: center;
  padding-inline-start: 24px;
  border-top: 1px solid ${greyMui._300};
  background-color: ${rydeColors.white};
  gap: 10px;
  transition:
    filter 0.55s ease-out,
    backdrop-filter 0.55s ease-out;
  &.inner-item-editing {
    filter: blur(2px) grayscale(100%);
    backdrop-filter: blur(3px);
    pointer-events: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 110px;
    padding-top: 10px;
    padding-inline-start: 13px;
    align-items: flex-start;
  }
`;

export const LayoutRightFooterFormWidthArea = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "footer-form-width-area",
  },
)`
  display: flex;
  align-items: center;
  width: 100%;
  margin-inline-end: 25px;
  gap: 12px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-inline-end: 10px;
  }
`;

export const LayoutRightFooterButtonsArea = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "footer-form-footer-buttons",
  },
)`
  margin-inline-start: auto;
  gap: 12px;
  display: flex;
  align-items: center;
  height: 40px;
`;

export const EditFormFooterButton = styled.button.attrs<IStyledAttributes>({
  "data-e2e": "footer-button",
})`
  line-height: normal;
  font-size: 16px;
  display: flex;
  cursor: pointer;
  color: ${greyMui._700};
  align-items: center;
  gap: 5px;
  border: none;
  background-color: inherit;
  &:hover {
    color: ${blueMui._700};
  }
  &:disabled {
    color: ${greyMui._500};
  }
  .footer_svg_icon {
    font-size: 16px;
  }
`;

export const LayoutRightResizeHandleStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "layout-right-resize-handle",
  },
)`
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: 8px;
  cursor: col-resize;
  user-select: none;
  z-index: ${ScreenDepth.LAYOUT_RIGHT_FOOTER + 1};
  /* Always visible, like the ride drawer splitters, so the panel reads as resizable. */
  background-color: ${greyMui._300};
  transition: background-color 0.15s ease;

  &:hover,
  &:active {
    background-color: ${greyMui._400};
  }

  &:focus-visible {
    outline: 2px solid ${blueMui._500};
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
